"use client";

import { Button } from "@watt/ui/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormWrapper
} from "@watt/ui/components/ui/form";
import { Input } from "@watt/ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@watt/ui/components/ui/select";
import { Switch } from "@watt/ui/components/ui/switch";
import { useZodForm } from "@watt/ui/hooks/use-zod-form";
import { useWatch } from "react-hook-form";
import { toast } from "sonner";
import { fireTestToast } from "./fire-test-toast";
import {
  DEFAULT_TOAST_TESTER_CONFIG,
  MAX_TOAST_TESTER_COUNT,
  TOAST_TESTER_PRESETS,
  TOAST_TESTER_TYPES,
  type ToastTesterConfig,
  toastTesterConfigSchema
} from "./toast-tester-config";
import { ToastTesterSwitchField } from "./toast-tester-switch-field";

/**
 * Configurable form for raising any sonner toast variant, with optional
 * action/cancel buttons, custom duration and bursts for stacking checks.
 */
export function ToastTester() {
  const form = useZodForm({
    defaultValues: DEFAULT_TOAST_TESTER_CONFIG,
    schema: toastTesterConfigSchema
  });
  const [type, isPersistent] = useWatch({
    control: form.control,
    name: ["type", "isPersistent"]
  });

  const applyPreset = (config: ToastTesterConfig) => {
    form.reset(config);
  };

  return (
    <FormWrapper className="space-y-3" form={form} handleSubmit={fireTestToast}>
      <div className="flex flex-wrap gap-1">
        {TOAST_TESTER_PRESETS.map(preset => (
          <Button
            className="h-6 px-2 text-xs"
            key={preset.label}
            onClick={() => applyPreset(preset.config)}
            type="button"
            variant="outline"
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs">Type</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-8 capitalize">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="z-[10000]">
                {TOAST_TESTER_TYPES.map(toastType => (
                  <SelectItem
                    className="capitalize"
                    key={toastType}
                    value={toastType}
                  >
                    {toastType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs">Title</FormLabel>
            <FormControl>
              <Input className="h-8" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-xs">Description</FormLabel>
            <FormControl>
              <Input className="h-8" placeholder="None" {...field} />
            </FormControl>
          </FormItem>
        )}
      />

      <ToastTesterSwitchField
        labelName="actionLabel"
        switchLabel="Action button"
        switchName="withAction"
      />
      <ToastTesterSwitchField
        labelName="cancelLabel"
        switchLabel="Cancel button"
        switchName="withCancel"
      />

      {type === "promise" && (
        <FormField
          control={form.control}
          name="shouldPromiseReject"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between gap-3 space-y-0">
              <FormLabel className="text-xs">Promise rejects</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  size="sm"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      <div className="grid grid-cols-2 gap-2">
        <FormField
          control={form.control}
          name="durationSeconds"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Duration (s)</FormLabel>
              <FormControl>
                <Input
                  className="h-8"
                  disabled={isPersistent}
                  max={60}
                  min={1}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={event => field.onChange(event.target.valueAsNumber)}
                  ref={field.ref}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="count"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Count</FormLabel>
              <FormControl>
                <Input
                  className="h-8"
                  max={MAX_TOAST_TESTER_COUNT}
                  min={1}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={event => field.onChange(event.target.valueAsNumber)}
                  ref={field.ref}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="isPersistent"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-3 space-y-0">
            <FormLabel className="text-xs">Persistent</FormLabel>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                size="sm"
              />
            </FormControl>
          </FormItem>
        )}
      />

      <div className="flex gap-2">
        <Button className="h-8 flex-1" size="sm" type="submit">
          Show toast
        </Button>
        <Button
          className="h-8"
          onClick={() => toast.dismiss()}
          size="sm"
          type="button"
          variant="outline"
        >
          Dismiss all
        </Button>
      </div>
    </FormWrapper>
  );
}
